import type React from 'react';
import { useTranslation } from 'react-i18next';
import { formatGel } from '@/utils/money';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';
import { MetricRow, OverviewCard, ThresholdBar } from '../overviewCard';
import { TONE_TEXT } from '../overviewMeta';
import {
    CASH_RUNWAY as META,
    RUNWAY_TICKS,
    RUNWAY_VERDICT_KEY,
} from './cashRunwayMeta';
import {
    RUNWAY_SEGMENTS,
    runwayMarker,
    type CashRunwayData,
} from './useCashRunway';

type CashRunwayViewProps = {
    data: CashRunwayData;
};

/**
 * Cash runway — render only.
 *
 * No verdict band, unlike its three siblings: the answer *is* the number, and
 * repeating "169 days" in a strip underneath would be the same fact twice. The
 * band's job — saying what the figure means — is done by the sentence under it
 * and by where the marker sits on the scale.
 */
export const CashRunwayView: React.FC<CashRunwayViewProps> = ({ data }) => {
    const { t } = useTranslation('pages');

    return (
        <OverviewCard
            delta={
                data.deltaDays === null
                    ? null
                    : {
                          label: `${data.deltaDays > 0 ? '+' : '-'}${t(META.keys.deltaDays, { count: Math.abs(data.deltaDays) })}`,
                          tone: data.deltaDays < 0 ? 'danger' : 'success',
                      }
            }
            icon={META.icon}
            question={t(META.keys.question)}
            slot={META.slot}
            title={t(META.keys.title)}
            tone={data.tone}
        >
            <div className="flex items-end justify-between gap-4">
                <div className="min-w-0 flex-1">
                    <ThresholdBar
                        ariaLabel={t(META.keys.scaleAria)}
                        marker={runwayMarker(data.days)}
                        segments={RUNWAY_SEGMENTS}
                        slot={`${META.slot}-scale`}
                        ticks={RUNWAY_TICKS}
                    />
                </div>
                <div className="flex flex-col items-end">
                    <span
                        className={`text-2xl font-semibold ${TONE_TEXT[data.tone]}`}
                        {...getNodeTestIdProp('cash-runway-days')}
                    >
                        {t(META.keys.days, { count: data.days })}
                    </span>
                    <span className="text-xs text-muted">
                        {t(RUNWAY_VERDICT_KEY[data.tone])}
                    </span>
                </div>
            </div>
            <MetricRow
                label={t(META.keys.cash)}
                slot={`${META.slot}-cash`}
                tone="success"
                value={formatGel(data.cashTetri)}
            />
            <MetricRow
                label={t(META.keys.dailyFixedCost)}
                slot={`${META.slot}-daily-fixed-cost`}
                value={formatGel(data.dailyFixedCostTetri)}
            />
        </OverviewCard>
    );
};
